import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlineIcon from '@mui/icons-material/LockOutline';

/**
 * Human-readable label for a permission id such as `bob.jobs:manage`.
 *
 * @param {string} permissionId
 * @returns {string}
 */
export const formatPermissionLabel = (permissionId) => {
  const id = String(permissionId || '');
  const withoutApp = id.includes('.') ? id.slice(id.indexOf('.') + 1) : id;
  const [resource = '', action = 'access'] = withoutApp.split(':');
  const words = resource.replace(/[._-]+/g, ' ').trim();
  const resourceLabel = words || 'this feature';
  const verbs = {
    approve: 'Approve',
    create: 'Create',
    delete: 'Delete',
    manage: 'Manage',
    read: 'View',
    run: 'Run',
    upload: 'Upload',
    write: 'Edit',
  };
  const verb = verbs[action] || action.replace(/[._-]+/g, ' ');
  return `${verb.charAt(0).toUpperCase()}${verb.slice(1)} ${resourceLabel}`;
};

/**
 * Drawer showing the current app's permission capabilities and requestable roles.
 */
export const PermissionCenterDrawer = ({
  open,
  onClose,
  appName,
  capabilities = [],
  roles = [],
  loading = false,
  error = null,
  onRefresh,
  onRequestRole,
}) => {
  const requestableRoles = roles.filter((role) => !role.granted);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, maxWidth: '100%' } }}
    >
      <Box role="dialog" aria-label={`${appName} permissions`} sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h6">Your permissions</Typography>
            <Typography variant="body2" color="text.secondary">
              Access available in {appName}
            </Typography>
          </Box>
          <IconButton aria-label="close permissions" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {loading && (
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading permissions…</Typography>
          </Stack>
        )}

        {!loading && error && (
          <Alert
            severity="error"
            action={
              onRefresh ? (
                <Button color="inherit" size="small" onClick={onRefresh}>
                  Retry
                </Button>
              ) : null
            }
          >
            We couldn&apos;t load your permissions.
          </Alert>
        )}

        {!loading && !error && (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Capabilities
            </Typography>
            {capabilities.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No permissions have been published for this app yet.
              </Typography>
            ) : (
              <List dense disablePadding sx={{ mt: 1 }}>
                {capabilities.map((capability) => (
                  <ListItem key={capability.id} disableGutters>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {capability.granted ? (
                        <CheckCircleOutlineIcon color="success" fontSize="small" />
                      ) : (
                        <LockOutlineIcon color="disabled" fontSize="small" />
                      )}
                    </ListItemIcon>
                    <ListItemText primary={capability.label} secondary={capability.id} />
                    <Chip
                      size="small"
                      color={capability.granted ? 'success' : 'default'}
                      label={capability.granted ? 'Granted' : 'Missing'}
                      variant={capability.granted ? 'filled' : 'outlined'}
                    />
                  </ListItem>
                ))}
              </List>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Request access
            </Typography>
            {requestableRoles.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {roles.length === 0
                  ? 'No requestable roles are available for this app.'
                  : 'You already have every capability offered by the available roles.'}
              </Typography>
            ) : (
              <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                {requestableRoles.map((role) => (
                  <Paper key={role.id} variant="outlined" sx={{ p: 2 }}>
                    <Stack
                      direction="row"
                      alignItems="flex-start"
                      justifyContent="space-between"
                      spacing={2}
                    >
                      <Box>
                        <Typography variant="subtitle2">{role.name || role.id}</Typography>
                        {role.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {role.description}
                          </Typography>
                        )}
                      </Box>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onRequestRole(role.id)}
                      >
                        Request
                      </Button>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default PermissionCenterDrawer;
